import { blockFocusInside, unblockFocus } from '@/scripts/lib/blockFocusInside';
import { getCoords } from '@/scripts/utils/base';
import scrollToElem from '@/scripts/lib/scrollToElem';
import Events from '@/scripts/lib/Events';

import './gigPopover.scss';

/** @typedef {[number, number, number, number]} Padding */
/** @typedef {'top'|'right'|'bottom'|'left'} PositionMode */
/** @typedef {PositionMode[]} Priority */

/**
 * @typedef {{
 *  trigger: DOMRect,
 *  width: number,
 *  height: number,
 *  docWidth: number,
 *  docHeight: number,
 *  maxX: number,
 *  maxY: number,
 *  minX: number,
 *  minY: number,
 * }} Metrics
 */

/**
 * @typedef {{
 *    offset: number,
 *    padding: Padding,
 *    arrowOffset: number,
 *    priority: Priority,
 *    openOn: 'none'|'click'|'hover',
 *    manageFocus: boolean,
 * }} Options
 */

/** @type {Options} */
const DEF_OPTIONS = {
   offset: 15,
   padding: [4, 4, 4, 4],
   arrowOffset: 16,
   priority: ['top', 'right', 'left', 'bottom'],
   openOn: 'click',
   manageFocus: true,
};

export default class GigPopover {

   /** @type {Events<'open'|'open-before'|'close'|'close-after'>} */
   events = new Events(); 

   _isOpened = false;

   /** @type {Metrics} */
   _metrics = {};

   _eventHandlers = {
      resize: null,
      keyup: null,
      click: null,
      orientation: null,
      mouseEnter: null,
      mouseLeave: null,
   };

   /**
    * @param {HTMLElement} root 
    * @param {HTMLElement} trigger 
    * @param {Options} options 
    */
   constructor(root, trigger, options = {}) {
      this._options = { ...DEF_OPTIONS, ...options };
      this.$trigger = trigger;
      this.$root = root;
      this._mode = this._options.priority[0];

      this._init();
   }

   _init() {
      this._initEvents();
      this._start();
   }

   _start() {
      this.$root.classList.add('gig-popover');
      this.$root.remove();
   }

   _initEvents() {
      const handlers = this._eventHandlers;
      let resizeFrame = 0;

      handlers.click = (e) => {
         if (this.$root.contains(e.target)) {
            return;
         } else if (this.$trigger.contains(e.target)
            && this._options.openOn === 'click') {
            e.preventDefault();
            requestAnimationFrame(() => this.toggle());
            return;
         }

         if (this._options.openOn === 'click') {
            this.close();
         }
      };

      if (this._options.openOn === 'hover') {
         handlers.mouseEnter = () => this.open();
         handlers.mouseLeave = () => this.close();

         this.$trigger.addEventListener('mouseenter', handlers.mouseEnter);
         this.$trigger.addEventListener('mouseleave', handlers.mouseLeave);
      }

      handlers.keyup = ({ code }) => {
         if (code === 'Escape') this.close();
      };

      handlers.resize = () => {
         cancelAnimationFrame(resizeFrame);
         resizeFrame = requestAnimationFrame(() => this.resize());
      };

      handlers.scroll = (event) => {
         this._eventHandlers.resize(event);
      };

      handlers.orientation = () => {
         requestAnimationFrame(() => this.resize());
         setTimeout(() => this.resize(), 250);
         setTimeout(() => this.resize(), 1000);
      };

      document.addEventListener('click', handlers.click);
      document.addEventListener('keyup', handlers.keyup);
      window.addEventListener('resize', handlers.resize);
      window.addEventListener('scroll', handlers.scroll, true);
      window.addEventListener('orientationchange', handlers.orientation);

      this.$root.addEventListener('click', (e) => {
         if (!e.target.closest('.popover-close-js')) return;
         e.preventDefault();
         this.close();
      });
   }

   open() {
      if (this._isOpened) return;
      this.events.emit('open.before');
      this._isOpened = true;

      this._activeElement = document.activeElement;
      document.body.appendChild(this.$root);

      this._clearStyles();
      this._updateFullscreenMode();
      this._updateMetrics();

      this.position();
      this._correctScroll();

      this.$root.classList.add('gig-popover__open');
      this.$root.setAttribute('aria-hidden', false);

      if (this._options.manageFocus) {
         setTimeout(() => blockFocusInside(this.$root), 60);
      }

      this.events.emit('open');
   }

   close() {
      if (!this._isOpened) return;
      this._isOpened = false;

      this.$root.classList.remove('gig-popover__open');
      this.$root.setAttribute('aria-hidden', true);

      if (this._options.manageFocus) {
         unblockFocus();

         if (this._activeElement) {
            this._activeElement.focus();
         }
      }

      this._turnOffFullscreenMode();

      setTimeout(() => {
         this.$root.remove();
         this.events.emit('close.after');
      }, 300);

      this.events.emit('close');
   }

   toggle() {
      return (this._isOpened) ? this.close() : this.open();
   }

   position() {
      this._positionRoot();
      this._positionArrow();
   }

   _positionRoot() {
      const { x, y, mode } = this._options.priority.map((mode, priority) => {
         const { x: ix, y: iy } = this._getCoords(mode);
         const { x, y } = this._correctCoords(ix, iy);

         const d = (mode === 'top' || mode === 'bottom') ? y - iy : x - ix;
         const val = Math.abs(d) + priority;

         return { x, y, mode, val };
      }).sort((a, b) => a.val - b.val)[0];

      this._mode = mode;
      this.$root.style.left = `${x ^ 0}px`;
      this.$root.style.top = `${y ^ 0}px`;
      this.$root.classList.add(`gig-popover__${this._mode}`);
   }

   _correctCoords(x, y) {
      x = Math.min(Math.max(x, this._metrics.minX), this._metrics.maxX);
      y = Math.min(Math.max(y, this._metrics.minY), this._metrics.maxY);
      return { x, y };
   }

   _positionArrow() {
      if (!('setProperty' in this.$root.style)) return;

      const { trigger, width, height } = this._metrics;
      const offset = this._options.arrowOffset;
      const pos = getCoords(this.$root);

      if (this._mode === 'bottom' || this._mode === 'top') {
         const val = trigger.left + trigger.width / 2 - pos.left;
         const realVal = Math.min(Math.max(val, offset), width - offset);
         this.$root.style.setProperty('--trigger-pos', `${realVal}px`);

      } else {
         const val = trigger.top + trigger.height / 2 - pos.top;
         const realVal = Math.min(Math.max(val, offset), height - offset);
         this.$root.style.setProperty('--trigger-pos', `${realVal}px`);
      }
   }

   /** 
    * @param {PositionMode} mode 
    * @returns {{x: number, y: number}}
    */
   _getCoords(mode) {
      const { trigger, width, height } = this._metrics;
      const { offset } = this._options;
      let x = 0, y = 0;

      if (mode === 'top') {
         x = trigger.left + trigger.width / 2 - width / 2;
         y = trigger.top - height - offset;
      } else if (mode === 'right') {
         x = trigger.right + offset;
         y = trigger.top + trigger.height / 2 - height / 2;
      } else if (mode === 'bottom') {
         x = trigger.left + trigger.width / 2 - width / 2;
         y = trigger.bottom + offset;
      } else if (mode === 'left') {
         x = trigger.left - width - offset;
         y = trigger.top + trigger.height / 2 - height / 2;
      } else {
         throw new TypeError(`Invalid position mode: ${mode}`);
      }

      return { x, y };
   }

   _updateMetrics() {
      const [pt, pr, pb, pl] = this._options.padding;
      const m = this._metrics;

      this.$root.style.display = 'none';
      m.docWidth = document.body.scrollWidth;
      m.docHeight = document.body.scrollHeight;
      this.$root.style.display = '';

      m.trigger = getCoords(this.$trigger);

      m.width = this.$root.offsetWidth;
      m.height = this.$root.offsetHeight;

      m.maxX = m.docWidth - m.width - pr - 1;
      m.maxY = m.docHeight - m.height - pb - 1;

      m.minX = pl;
      m.minY = pt;
   }

   async _correctScroll() {
      const pos = this.$root.getBoundingClientRect();

      const html = document.documentElement;
      const winHeight = window.innerHeight || html.clientHeight;

      if (pos.top < 0) {
         await scrollToElem(this.$root, 1, 'top');
      } else if (pos.bottom > winHeight) {
         await scrollToElem(this.$root, 1, 'bottom');
      }
   }

   _updateFullscreenMode() {
      if (this._getMode() === 'fullscreen') {
         this._turnOnFullscreenMode();
         return;
      } else {
         this._turnOffFullscreenMode();
      }
   }

   _turnOnFullscreenMode() {
      this.$root.classList.add('gig-popover__fullscreen');
      document.body.classList.add('body__gig-popover-fullscreen');
   }

   _turnOffFullscreenMode() {
      this.$root.classList.remove('gig-popover__fullscreen');
      document.body.classList.remove('body__gig-popover-fullscreen');
   }

   _clearStyles() {
      this.$root.style.left = 0;
      this.$root.style.top = 0;

      ['left', 'top', 'right', 'bottom', 'fullscreen'].forEach((item) => {
         this.$root.classList.remove(`gig-popover__${item}`);
      });
   }

   _getMode() {
      const computed = getComputedStyle(this.$root);
      if (!('getPropertyValue' in computed)) return false;

      return computed.getPropertyValue('--gig-popover-mode').trim();
   }

   resize() {
      if (!this._isOpened) return;

      this._clearStyles();
      this._updateFullscreenMode();
      this._updateMetrics();
      this.position();

   }

   destroy() {
      this.close();

      const { click, keyup, resize, scroll, orientation } = this._eventHandlers;
      const { mouseEnter, mouseLeave } = this._eventHandlers;

      window.removeEventListener('orientationchange', orientation);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', scroll);
      document.removeEventListener('click', click);
      document.removeEventListener('keyup', keyup);
      this.$trigger.addEventListener('mouseenter', mouseEnter);
      this.$trigger.addEventListener('mouseleave', mouseLeave);
   }
}